import {DownOutlined, FileDoneOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Layout,
  Result,
  Skeleton,
  Table,
  notification,
  theme, Dropdown, Space, Modal, Form, Input
} from "antd";
import {useEffect, useState} from "react";
import { Link, useNavigate } from "react-router-dom";
import {apiPointOfSaleDownload, apiPointOfSaleList, apiTerminalRegister} from "../../../../api/pointOfSale";
import { useIsMobileHook } from "../../../../utils/WindowHooks";
import { formatDate } from "../../../../utils/format";
import AccountsSearchSelect from "../../../common/AccountsSearchSelect/AccountsSearchSelect";
import "./PointOfSaleListing.scss";
import { userHasPermission } from "../../../../helpers/user";

const { Content } = Layout;

const PointOfSaleListing = () => {
  const [dataSales, setDataSale] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const isMobile = useIsMobileHook(1600);
  const [filteredData, setFilteredData] = useState(dataSales?.results);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [sellerFilter, setSellerFilter] = useState();
  const [registerModalVisible, setRegisterModalVisible] = useState(false);
  const [registerForm] = Form.useForm();
  const [registerLoading, setRegisterLoading] = useState(false);

  const handleRowClick = (record) => {
    const id = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(id);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(id);
    }
    setExpandedRowKeys(newExpandedRowKeys);
  };

  useEffect(() => {
    setPagination({
      current: 1,
      total: 0,
    })
    fetchData({
      acquirer_account: sellerFilter ? sellerFilter.id : undefined,
    });
  }, [sellerFilter]);

  const fetchData = async (params) => {
    setIsFetching(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const json = await apiPointOfSaleList(filteredParams);
      setDataSale(json);
      setFilteredData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: params.page,
      }));
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar os terminais POS.",
        placement: "topRight",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const onChangePage = (pagination) => {
    fetchData({
      page: pagination.current,
      pageSize: pagination.pageSize,
      acquirer_account: sellerFilter ? sellerFilter.id : undefined,
    });
  };

  const statusMap = {
    active: <span className="badge badge-success">Ativo</span>,
    inactive: <span className="badge badge-error">Inativo</span>,
    pending: <span className="badge orange">Pendente</span>,
  };

  const renderStatus = (status) => {
    return statusMap[status] || <span className="badge black">Indefinido</span>;
  };

  const items = [
    {
      label: 'CSV',
      key: 'csv',
      icon: <FileDoneOutlined />,
    },
    {
      label: 'EXCEL',
      key: 'excel',
      icon: <FileDoneOutlined />,
    },
  ];

  const handleDownload = async (format) => {
    const filters = {
      acquirer_account: sellerFilter ? sellerFilter.id : undefined,
    };

    const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined)
    );

    try {
      setDownloadLoading(true);
      const blob = await apiPointOfSaleDownload(format, filteredParams);
      const date = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Terminais-POS-${date}.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const reportMenuProps = {
    items,
    onClick: (e) => {
      handleDownload(e.key);
    },
  };

  const handleRegisterTerminal = async () => {
    try {
      setRegisterLoading(true);
      const values = await registerForm.validateFields();
      await apiTerminalRegister(values.selectedAccount.id, values.token);
      notification.success({
        message: "Sucesso",
        description: "Terminal registrado com sucesso!",
        placement: "topRight",
      });
      setRegisterModalVisible(false);
      registerForm.resetFields();
      fetchData({
        acquirer_account: sellerFilter ? sellerFilter.id : undefined,
        page: 1,
      });
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao registrar o terminal.",
        placement: "topRight",
      });
    } finally {
      setRegisterLoading(false);
    }
  };

  const columns = [
    {
      title: "Número de Série",
      dataIndex: "serial_number",
      key: "serial_number",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (serial) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: serial }}
        />
      ),
    },
    {
      title: "Fabricante",
      dataIndex: "manufacturer",
      key: "manufacturer",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (manufacturer) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: manufacturer || "N/A" }}
        />
      ),
      responsive: ["sm"],
    },
    {
      title: "Modelo do Terminal",
      dataIndex: "terminal_model",
      key: "terminal_model",
      responsive: ["lg"],
      render: (model) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: model || "N/A" }}
        />
      ),
    },
    {
      title: "Conta Base",
      dataIndex: ["acquirer_account", "display_name"],
      key: "acquirer_account__display_name",
      responsive: ["xxl"],
    },
    {
      title: "Última Transação",
      dataIndex: "last_tx_at",
      key: "last_tx_at",
      responsive: ["sm"],
      render: (lastTx) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: lastTx ? formatDate(lastTx) : "N/A" }}
        />
      ),
    },
    {
      title: "Data da Criação",
      dataIndex: "created_at",
      key: "created_at",
      responsive: ["lg"],
      render: (created) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: formatDate(created) }}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
    },
    {
      title: "",
      dataIndex: "",
      render: (terminal) => (
        <Link to={`/terminal-pos/${terminal.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      ),
      key: "",
      responsive: ["lg"],
    },
  ];

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
       Número de Série:{" "}
        <p
          dangerouslySetInnerHTML={{ __html: record.serial_number }}
        />
      </span>
      <span>
        Fabricante:{" "}
        <p>{record.manufacturer || "N/A"}</p>
      </span>
      <span>
        Modelo:{" "}
        <p>{record.terminal_model || "N/A"}</p>
      </span>
      <span>
        Conta Base:{" "}
        <p>{record.acquirer_account ? record.acquirer_account.display_name : ""}</p>
      </span>
      <span>
        Última Transação:{" "}
        <p dangerouslySetInnerHTML={{ __html: record.last_tx_at ? formatDate(record.last_tx_at) : "N/A" }} />
      </span>
      <span>
        Status:{" "}
        {renderStatus(record.status)}
      </span>
      <span style={{ marginTop: "10px" }}>
        Informação:{" "}
        <Link to={`/terminal-pos/${record.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      </span>
    </div>
  );
  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  return (
    <div className="PointOfSaleListing">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Terminais POS</h2>
          </div>
          <Divider />
          <Breadcrumb.Item>Operação</Breadcrumb.Item>
          <Breadcrumb.Item>Terminais POS</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="subtitle-page">Listagem de terminais POS</h3>
            {userHasPermission("add_pos") && <Button type="primary" onClick={() => setRegisterModalVisible(true)}>
              Registrar Terminal
            </Button>}
          </div>
          <Divider />
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <AccountsSearchSelect
                style={{ width: '200px'}}
                placeholder="Filtrar por conta"
                value={sellerFilter}
                onChange={(value) => setSellerFilter(value)}
                allowClear={true}
              />
            </div>            
          </div>
          {isFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError(errorStatus)
          ) : (
            <Table
              columns={columns}
              locale={locale}
              expandable={{
                expandedRowRender: isMobile ? expandedRowRender : undefined,
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => handleRowClick(record),
                responsive: ["xl"],
              }}
              dataSource={
                filteredData &&
                filteredData.map((item) => ({ ...item, key: item.id }))
              }
              pagination={{
                current: pagination.current,
                total: pagination.total,
                pageSize: pagination.pageSize,
                showSizeChanger: false,
              }}
              bordered
              onChange={onChangePage}
            />
          )}
          <div className="download-section">
            <Dropdown menu={reportMenuProps} disabled={downloadLoading}>
              <Button loading={downloadLoading} disabled={downloadLoading}>
                <Space>
                  Baixar Relatório
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </Content>
        <Modal
          title="Registrar Terminal"
          open={registerModalVisible}
          onOk={handleRegisterTerminal}
          onCancel={() => {
            setRegisterModalVisible(false);
            registerForm.resetFields();
          }}
          confirmLoading={registerLoading}
          okText="Associar"
          cancelText="Cancelar"
        >
          <Form
            form={registerForm}
            layout="vertical"
            name="registerTerminal"
          >
            <Form.Item
              name="selectedAccount"
              label="Selecionar Vendedor"
              rules={[
                {
                  required: true,
                  message: "Por favor, selecione um vendedor!",
                },
              ]}
            >
              <AccountsSearchSelect
                placeholder="Selecionar vendedor"
                allowClear={false}
              />
            </Form.Item>
            <Form.Item
              name="token"
              label="Token do Terminal"
              rules={[
                {
                  required: true,
                  message: "Por favor, insira o token do terminal!",
                },
              ]}
            >
              <Input placeholder="Insira o token do terminal" />
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </div >
  );
};

export default PointOfSaleListing;
